<template>
  <div class="page-container customer-terms-page">
    <portal to="page-name">Termos de uso</portal>

    <div class="card card-page">
      <div class="card-header">
        <h1 class="card-title">Termos de uso</h1>
      </div>

      <div class="card-body">
        <div class="loading loading-lg mt-2" v-if="loading" />
        <template v-else-if="terms">
          <div
            v-if="!isMigrated"
            class="terms-status"
            :class="terms.accepted ? 'is-accepted' : 'is-pending'"
          >
            <div class="terms-status-icon">
              <fa-icon :icon="['fal', terms.accepted ? 'check-circle' : 'clock']" />
            </div>
            <div class="terms-status-content">
              <template v-if="terms.accepted">
                <div class="terms-status-title">Termos aceitos</div>
                <div class="terms-status-detail">
                  Aceito em <strong>{{ terms.acceptedAt | date('datetime') }}</strong>
                  <template v-if="terms.acceptedBy">
                    por <strong>{{ terms.acceptedBy.name }}</strong>
                    <span v-if="terms.acceptedBy.email" class="ml-1">
                      ({{ terms.acceptedBy.email }})
                    </span>
                  </template>
                </div>
              </template>
              <template v-else>
                <div class="terms-status-title">Aceite pendente</div>
                <div class="terms-status-detail" v-if="terms.responsible">
                  Aguardando o aceite de <strong>{{ terms.responsible.name }}</strong>,
                  usuário master da conta.
                </div>
                <div class="terms-status-detail" v-else>
                  O responsável pela conta ainda não aceitou a versão atual dos termos.
                </div>
              </template>
            </div>
            <div class="terms-status-version" v-if="showAcceptedDate">
              Última atualização em {{ terms.acceptedAt | date }}
            </div>
            <button
              v-if="!terms.accepted && canAccept"
              type="button"
              class="btn btn-primary"
              :class="{ loading: accepting }"
              :disabled="accepting"
              @click="accept"
            >
              Aceitar termos
            </button>
          </div>

          <template v-if="history.length > 0">
            <h6 class="terms-history-title">Histórico de aceites</h6>
            <table class="table table-striped table-hover terms-history">
              <thead>
              <tr>
                <th>Versão dos termos</th>
                <th>Aceito em</th>
                <th>Aceito por</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, i) in history" :key="i">
                <td>
                  {{ item.version | date }}
                  <div class="text-small text-gray" v-if="item.summary">{{ item.summary }}</div>
                </td>
                <td>{{ item.acceptedAt | date('datetime') }}</td>
                <td>
                  <template v-if="item.acceptedBy">
                    {{ item.acceptedBy.name }}
                    <div class="text-small text-gray" v-if="item.acceptedBy.email">
                      {{ item.acceptedBy.email }}
                    </div>
                  </template>
                  <template v-else>-</template>
                </td>
              </tr>
              </tbody>
            </table>
          </template>

          <button
            type="button"
            class="btn btn-icon btn-icon-left btn-gray terms-print-button"
            @click="print"
          >
            <fa-icon :icon="['fal', 'print']" />Imprimir
          </button>

          <div class="divider" />

          <div id="customer-terms-print">
            <terms-content />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import TermsContent from '@/components/terms/TermsContent.vue';
import printTerms from '@/components/terms/printTerms';
import formatDate from '@/filters/date';
import logoUrl from '@/assets/svg/logo-stenci.svg';

export default {
  components: {
    TermsContent,
  },
  data() {
    return {
      loading: true,
      accepting: false,
      terms: null,
    };
  },
  mounted() {
    this.load();
  },
  computed: {
    canAccept() {
      return this.$store.state.auth.user?.terms?.canAccept === true;
    },
    isMigrated() {
      return !!this.terms?.accepted && this.terms.source === 'migration';
    },
    showAcceptedDate() {
      return !!this.terms?.accepted && !this.isMigrated && !!this.terms.acceptedAt;
    },
    history() {
      return this.terms?.history || [];
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await this.$http.get('/customer/terms');
        this.terms = data;
      } catch (e) {
        this.$toast.error(e);
      }
      this.loading = false;
    },
    print() {
      const body = this.$el.querySelector('#customer-terms-print .terms-content-body');
      if (!body) return;

      const acceptedBy = this.terms.acceptedBy || {};

      printTerms({
        title: 'Termos de uso do sistema Stenci',
        updatedLabel: `Última atualização em ${formatDate(this.terms.version)}`,
        contentHtml: body.innerHTML,
        logoUrl: new URL(logoUrl, window.location.origin).href,
        acceptance: this.showAcceptedDate ? {
          date: formatDate(this.terms.acceptedAt, 'datetime'),
          name: acceptedBy.name,
          email: acceptedBy.email,
        } : null,
        printedAt: formatDate(new Date(), 'datetime'),
      });
    },
    async accept() {
      this.accepting = true;
      try {
        this.terms = await this.$store.dispatch('acceptTerms');
        this.$toast.success('Termos de uso aceitos.');
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.accepting = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';

.customer-terms-page {
  .terms-status {
    align-items: center;
    border: $border-width solid;
    border-left-width: .2rem;
    border-radius: $border-radius;
    display: flex;
    gap: $unit-4;
    padding: $unit-4;
    &.is-accepted {
      background-color: #F2FAF5;
      border-color: #BFE3CD;
      border-left-color: #2FA36B;
      .terms-status-icon {
        color: #2FA36B;
      }
      .terms-status-title {
        color: #1F7A4E;
      }
    }
    &.is-pending {
      background-color: #FFF8E6;
      border-color: #F2C94C;
      border-left-color: $warning-color;
      .terms-status-icon {
        color: $warning-color;
      }
    }
  }
  .terms-status-icon {
    font-size: $unit-8;
    line-height: 1;
  }
  .terms-status-content {
    flex: 1;
  }
  .terms-status-title {
    color: $gray-color-900;
    font-size: $unit-4;
    font-weight: 700;
  }
  .terms-status-detail {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin-top: $unit-h;
  }
  .terms-status-version {
    background-color: $light-color;
    border: $border-width solid $border-color;
    border-radius: 1rem;
    color: $gray-color-dark;
    font-size: $font-size-xs;
    padding: $unit-h $unit-3;
    white-space: nowrap;
  }
  .terms-history-title {
    margin: $unit-6 0 $unit-2;
  }
  .terms-history {
    font-size: $font-size-sm;
  }
  .terms-print-button {
    margin-top: $unit-4;
  }
  .terms-content {
    font-size: .7rem;
    max-width: $control-width-lg;
    .terms-content-body {
      margin-top: $unit-4;
    }
  }
}
</style>
